import { lazy, useEffect, type PropsWithChildren } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Outlet, createBrowserRouter, useNavigate } from 'react-router-dom';
import { AuthProvider } from '@gov-nx/auth/web/providers/AuthProvider';
import {
	AutorizaceContextProvider,
	useAutorizaceContext,
} from '@gov-nx/component/common/Autorizace/Context';
import { AuthorizationDialog } from '@gov-nx/component/web/Autorizace/AuthorizationDialog/AuthorizationDialog';
import { useAuthorizationReturnUrl } from '@gov-nx/component/web/Autorizace/useAuthorizationReturnUrl';
import { useFailedSubmissionRedirect } from '@gov-nx/component/web/Autorizace/useFailedSubmissionRedirect';
import { RouteErrorBoundary } from '@gov-nx/component/web/State/RouteErrorBoundary';
import { useExtraConfigContext } from '@gov-nx/core/app/extra-config/ExtraConfigProvider';
import type { ExtraConfig } from '@gov-nx/core/app/extra-config/context.types';
import { SyncProvider } from '@gov-nx/core/app/sync/SyncProvider';
import { UrlErrorListener } from '@gov-nx/web/Listeners/UrlErrorListener';
import { ScrollToTopHelper } from '@gov-nx/web/Router/ScrollToTopHelper';
import { pageRouteResolver } from '@gov-nx/web/Router/routeResolver';
import { TabTrackingProvider } from '@gov-nx/web/TabTracking/TabTrackingProvider';
import { appLoaderControl } from '../helpers/loader';
import { getAgendasRoutes } from '../modules/Agendas/Routes';
import { getAuthRoutes } from '../modules/Auth/Routes';
import { getCadastreRoutes } from '../modules/Cadastre/Routes';
import { getConsentManagementRoutes } from '../modules/ConsentManagement/Routes';
import { getDataBoxRoutes } from '../modules/DataBox/Routes';
import { getDriversRegistryRoutes } from '../modules/DriversRegistry/Routes';
import { getPetitionRoutes } from '../modules/EPetition/Routes';
import { getNotificationRoutes } from '../modules/Notifications/Routes';
import { getPageRoutes } from '../modules/Page/Routes';
import { getProfileRoutes } from '../modules/Profile/Routes';
import { getServiceRoutes } from '../modules/Service/Routes';
import { getSettingsRoutes } from '../modules/Settings/Routes';
import { getSubmissionsRoutes } from '../modules/Submissions/Routes';
import { MatomoInstanceProvider } from '../providers/MatomoInstanceProvider';

const AppListeners = lazy(() =>
	import('../components/AppListeners').then(({ AppListeners }) => ({
		default: AppListeners,
	}))
);

const AppLayout = lazy(() =>
	import('../layouts/AppLayout').then(({ AppLayout }) => ({
		default: AppLayout,
	}))
);
const PublicLayout = lazy(() =>
	import('../layouts/PublicLayout').then(({ PublicLayout }) => ({
		default: PublicLayout,
	}))
);
const NotFoundPage = lazy(() =>
	import('../modules/Page/NotFoundPage').then(({ NotFoundPage }) => ({
		default: NotFoundPage,
	}))
);

const AppWrapper = ({ children }: PropsWithChildren) => {
	useEffect(() => {
		appLoaderControl.hide();
	}, []);

	return children;
};

const RootOutlet = () => {
	const authContext = useAutorizaceContext();
	const {
		extraConfig: {
			authorization: { enabled: isAuthorizationEnabled },
		},
	} = useExtraConfigContext();

	return (
		<AppWrapper>
			{isAuthorizationEnabled && authContext?.authorizationDialogOpen && (
				<AuthorizationDialog />
			)}
			<Outlet />
		</AppWrapper>
	);
};

const ErrorElement = () => (
	<AppWrapper>
		<RouteErrorBoundary />
	</AppWrapper>
);

const Root = () => {
	const {
		extraConfig: {
			authorization: { enabled: isAuthorizationEnabled },
		},
	} = useExtraConfigContext();
	const navigate = useNavigate();
	const returnUrl = useAuthorizationReturnUrl();
	const { redirectToFailedSubmission } = useFailedSubmissionRedirect();

	return (
		<MatomoInstanceProvider>
			<TabTrackingProvider>
				<AuthProvider>
					<SyncProvider enableServicesList={isAuthorizationEnabled}>
						{isAuthorizationEnabled ? (
							<AutorizaceContextProvider
								onConfirmAuthCodeSuccess={() =>
									navigate(pageRouteResolver('moje-podani'))
								}
								onCodeAuthorizationFailed={redirectToFailedSubmission}
								returnUrl={returnUrl}>
								<RootOutlet />
							</AutorizaceContextProvider>
						) : (
							<RootOutlet />
						)}
					</SyncProvider>
				</AuthProvider>
				<ScrollToTopHelper />
				<AppListeners />
				<UrlErrorListener />
			</TabTrackingProvider>
		</MatomoInstanceProvider>
	);
};

const getRoutes = (extraConfig: ExtraConfig): RouteObject[] => [
	{
		element: <Root />,
		errorElement: <ErrorElement />,
		children: [
			{
				element: <PublicLayout />,
				children: getAuthRoutes(),
			},
			{
				element: <AppLayout />,
				children: [
					...getPageRoutes(extraConfig.pageRoutes),
					...getServiceRoutes(extraConfig.serviceRoutes),
					...getSettingsRoutes(extraConfig.settingsRoutes),
					...getProfileRoutes(extraConfig.profileRoutes),
					...getSubmissionsRoutes(extraConfig.submissionsRoutes),
					...getPetitionRoutes(),
					...getCadastreRoutes(),
					...getDriversRegistryRoutes(),
					...getNotificationRoutes(extraConfig.notificationRoutes),
					...getAgendasRoutes(),
					...getConsentManagementRoutes(),
					{
						path: '*',
						element: <NotFoundPage />,
					},
				],
			},
			...getDataBoxRoutes(),
			{
				path: '*',
				element: <NotFoundPage />,
			},
		],
	},
];

export const createAppRouter = (extraConfig: ExtraConfig) =>
	createBrowserRouter(getRoutes(extraConfig));
